import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableImage, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  ShoppingBag,
  Users,
  Heart,
  ArrowRight,
  Calendar,
  Star,
  CheckCircle2,
  Eye,
  MessageCircle,
  Bell,
  Clock,
  Image,
  Ruler,
  Gem,
  Shirt,
  Palette,
  Tag,
  Gift,
  Package,
  Sparkles,
  TrendingUp,
  Layers,
  Crown,
  Check,
  X,
  AlertCircle,
  Info,
  Footprints,
  Watch,
  Glasses
} from "lucide-react";

const RetailSalesPage = () => {
  const [demoTab, setDemoTab] = useState("customer");
  const [flowStep, setFlowStep] = useState(1);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Retail & Fashion";
  }, []);

  // Retail categories and subcategories
  const retailCategories = [
    { name: "Clothing", icon: Shirt, items: ["Tops", "Bottoms", "Dresses", "Outerwear", "Activewear"] },
    { name: "Shoes", icon: Footprints, items: ["Sneakers", "Heels", "Boots", "Sandals", "Flats"] },
    { name: "Accessories", icon: Watch, items: ["Bags", "Belts", "Hats", "Scarves", "Sunglasses"] },
    { name: "Jewelry", icon: Gem, items: ["Rings", "Necklaces", "Bracelets", "Earrings", "Watches"] },
  ];

  // Size guide preview
  const sizeGuide = {
    clothing: {
      women: ["XXS", "XS", "S", "M", "L", "XL", "XXL", "1X", "2X", "3X", "4X", "5X"],
      men: ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"]
    },
    shoes: {
      women: ["5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "11", "12"],
      men: ["6", "7", "8", "9", "10", "11", "12", "13", "14", "15"]
    }
  };

  // Style themes
  const styleThemes = [
    { name: "Modern Minimalist", color: "#1a1a2e", desc: "Clean lines, neutral palette" },
    { name: "Streetwear", color: "#ec4899", desc: "Bold, urban, trendy" },
    { name: "Bohemian", color: "#92400e", desc: "Free-spirited, earthy" },
    { name: "Classic", color: "#1e40af", desc: "Timeless elegance" },
    { name: "Athleisure", color: "#059669", desc: "Sporty comfort" },
    { name: "Glamorous", color: "#9333ea", desc: "Statement pieces" },
    { name: "Monochrome", color: "#374151", desc: "Single color palettes" },
  ];

  // Jewelry materials
  const jewelryMaterials = [
    { name: "Gold", types: ["10K", "14K", "18K", "24K"], desc: "Classic yellow gold" },
    { name: "White Gold", types: ["14K", "18K"], desc: "Rhodium-plated elegance" },
    { name: "Rose Gold", types: ["14K", "18K"], desc: "Romantic pink hue" },
    { name: "Sterling Silver", types: ["925"], desc: "Timeless silver" },
    { name: "Platinum", types: ["950"], desc: "Premium durability" },
  ];

  // Mock customer preferences
  const mockCustomerPrefs = {
    name: "Customer",
    defaults: {
      size: {
        clothing: "M (Women's 8-10)",
        shoes: "8.5 US",
        ring: "7"
      },
      style: "Modern Minimalist",
      colors: ["Black", "Navy", "White", "Cream"],
      brands: ["Everlane", "COS", "& Other Stories"],
      budget: "Mid-range ($$)"
    },
    thisVisit: {
      occasion: "Work wardrobe refresh",
      needs: ["Blazer", "Trousers", "Versatile tops"],
      budget: "$500",
      notes: "Looking for pieces that transition from office to dinner"
    }
  };

  // Booking flow steps
  const flowSteps = [
    { num: 1, title: "Discover Your Store", desc: "Customer finds you online or walks in" },
    { num: 2, title: "Share Preferences", desc: "Size, style, and budget captured" },
    { num: 3, title: "Personalized Experience", desc: "You see their preferences instantly" },
    { num: 4, title: "Build Relationship", desc: "Save favorites, suggest new arrivals" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="retail-sales-page">
      <EarlyAccessModal 
        isOpen={showEarlyAccessModal} 
        onClose={() => setShowEarlyAccessModal(false)}
        category="style"
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-indigo-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl text-gray-900">phygital</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-700 hover:text-indigo-600">
                Sign In
              </Button>
            </Link>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="retail-get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-indigo-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-purple-300/20 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto text-center relative">
          <Badge className="mb-6 bg-indigo-100 text-indigo-700 border-indigo-200 px-4 py-2">
            <ShoppingBag className="w-4 h-4 mr-2 inline" />
            For Retail & Fashion
          </Badge>
          
          <EditableText 
            id="retail-hero-title"
            defaultValue="Know Your Customers Before They Walk In"
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            as="h1"
          />
          
          <EditableText
            id="retail-hero-subtitle"
            defaultValue="Capture sizes, style preferences, and shopping history. Create personalized experiences that build loyalty and drive repeat visits."
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-10"
            as="p"
          />
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-14 text-lg"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="retail-hero-cta"
            >
              List Your Store
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 rounded-full px-8 h-14 text-lg"
            >
              See How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Every Fashion Category, One Platform
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Whether you sell clothing, shoes, accessories, or jewelry - track customer preferences and sizes all in one place.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {retailCategories.map((category) => (
              <Card key={category.name} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-indigo-200">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors">
                    <category.icon className="w-7 h-7 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{category.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    {category.items.map((item) => (
                      <Badge key={item} variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Size Guide Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-700 border-purple-200">
              <Ruler className="w-4 h-4 mr-2 inline" />
              Size Intelligence
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Complete Size Management
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              US, UK, and EU sizes for clothing and shoes. Ring sizes, hat sizes, and more - all captured automatically.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Clothing Sizes */}
            <Card className="overflow-hidden">
              <CardHeader className="bg-indigo-50 border-b">
                <CardTitle className="flex items-center gap-2 text-indigo-700">
                  <Shirt className="w-5 h-5" />
                  Clothing Sizes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Women's (incl. Plus Sizes)</p>
                    <div className="flex flex-wrap gap-2">
                      {sizeGuide.clothing.women.map((size) => (
                        <Badge key={size} variant="outline" className="text-xs">{size}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Men's</p>
                    <div className="flex flex-wrap gap-2">
                      {sizeGuide.clothing.men.map((size) => (
                        <Badge key={size} variant="outline" className="text-xs">{size}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Shoe Sizes */}
            <Card className="overflow-hidden">
              <CardHeader className="bg-purple-50 border-b">
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Footprints className="w-5 h-5" />
                  Shoe Sizes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Women's US</p>
                    <div className="flex flex-wrap gap-2">
                      {sizeGuide.shoes.women.map((size) => (
                        <Badge key={size} variant="outline" className="text-xs">{size}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Men's US</p>
                    <div className="flex flex-wrap gap-2">
                      {sizeGuide.shoes.men.map((size) => (
                        <Badge key={size} variant="outline" className="text-xs">{size}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Style Themes Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-pink-100 text-pink-700 border-pink-200">
              <Palette className="w-4 h-4 mr-2 inline" />
              Style Matching
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Match Customers to Their Aesthetic
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Capture style preferences to curate personalized recommendations and new arrival alerts.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {styleThemes.map((theme) => (
              <div 
                key={theme.name} 
                className="p-4 rounded-xl border-2 hover:border-indigo-200 transition-all cursor-pointer group"
              >
                <div 
                  className="w-12 h-12 rounded-full mb-3 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: theme.color }}
                />
                <h3 className="font-semibold text-gray-900">{theme.name}</h3>
                <p className="text-sm text-gray-500">{theme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jewelry Materials Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">
              <Gem className="w-4 h-4 mr-2 inline" />
              Jewelry Specialists
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Track Materials & Preferences
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Gold, silver, platinum - know your customer's metal and stone preferences for perfect recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {jewelryMaterials.map((material) => (
              <Card key={material.name} className="text-center hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <Crown className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">{material.name}</h3>
                  <p className="text-xs text-gray-500 mb-2">{material.desc}</p>
                  <div className="flex flex-wrap justify-center gap-1">
                    {material.types.map((type) => (
                      <Badge key={type} className="text-xs bg-amber-100 text-amber-700">{type}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <section className="py-20 px-4 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-700 border-indigo-200">
              <Eye className="w-4 h-4 mr-2 inline" />
              Live Demo
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              See Both Sides of the Experience
            </h2>
          </div>

          <Tabs defaultValue="customer" className="w-full" onValueChange={setDemoTab}>
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="customer" className="text-sm">
                <Users className="w-4 h-4 mr-2" />
                Customer View
              </TabsTrigger>
              <TabsTrigger value="retailer" className="text-sm">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Your Dashboard
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customer">
              <Card className="max-w-lg mx-auto shadow-xl">
                <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Your Fashion Profile</h3>
                      <p className="text-sm text-white/80">Personalized shopping experience</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">My Sizes</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Clothing</p>
                        <p className="font-semibold text-indigo-600">{mockCustomerPrefs.defaults.size.clothing}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Shoes</p>
                        <p className="font-semibold text-indigo-600">{mockCustomerPrefs.defaults.size.shoes}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Ring</p>
                        <p className="font-semibold text-indigo-600">{mockCustomerPrefs.defaults.size.ring}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">My Style</h4>
                    <Badge className="bg-indigo-100 text-indigo-700 mb-2">
                      {mockCustomerPrefs.defaults.style}
                    </Badge>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {mockCustomerPrefs.defaults.colors.map((color) => (
                        <Badge key={color} variant="outline" className="text-xs">{color}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Favorite Brands</h4>
                    <div className="flex flex-wrap gap-2">
                      {mockCustomerPrefs.defaults.brands.map((brand) => (
                        <Badge key={brand} variant="secondary" className="text-xs">{brand}</Badge>
                      ))}
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-indigo-900 mb-2">
                      Today's Shopping List
                    </h4>
                    <p className="text-sm text-indigo-700 mb-2">{mockCustomerPrefs.thisVisit.occasion}</p>
                    <div className="flex flex-wrap gap-2">
                      {mockCustomerPrefs.thisVisit.needs.map((item) => (
                        <Badge key={item} className="bg-indigo-600 text-white text-xs">{item}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="retailer">
              <Card className="max-w-3xl mx-auto shadow-xl">
                <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Customer Walking In</h3>
                        <p className="text-sm text-white/80">Profile: Regular Customer</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500 text-white">VIP</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                          Saved Preferences
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-gray-600">Size</span>
                            <span className="font-medium">{mockCustomerPrefs.defaults.size.clothing}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-gray-600">Shoe Size</span>
                            <span className="font-medium">{mockCustomerPrefs.defaults.size.shoes}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-gray-600">Style</span>
                            <span className="font-medium">{mockCustomerPrefs.defaults.style}</span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600">Budget</span>
                            <span className="font-medium">{mockCustomerPrefs.defaults.budget}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                        <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                          <Info className="w-4 h-4" />
                          Today's Visit
                        </h4>
                        <p className="text-sm text-amber-700 mb-2">{mockCustomerPrefs.thisVisit.occasion}</p>
                        <p className="text-sm text-amber-600">Budget: {mockCustomerPrefs.thisVisit.budget}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {mockCustomerPrefs.thisVisit.needs.map((item) => (
                            <Badge key={item} className="bg-amber-200 text-amber-800 text-xs">{item}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <h4 className="text-sm font-semibold text-indigo-800 mb-2">
                          Notes
                        </h4>
                        <p className="text-sm text-indigo-700">{mockCustomerPrefs.thisVisit.notes}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                          <Check className="w-4 h-4 mr-2" />
                          Check In
                        </Button>
                        <Button size="sm" variant="outline">
                          <Package className="w-4 h-4 mr-2" />
                          View History
                        </Button>
                      </div>
                      <div className="text-sm text-gray-500">
                        Last visit: 2 weeks ago
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            The Phygital Flow
          </h2>
          
          <div className="space-y-8">
            {flowSteps.map((step, index) => (
              <div 
                key={step.num}
                className={`flex items-start gap-6 p-6 rounded-2xl transition-all ${
                  flowStep === step.num ? 'bg-indigo-50 border-2 border-indigo-200' : 'hover:bg-gray-50'
                }`}
                onClick={() => setFlowStep(step.num)}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shrink-0 ${
                  flowStep === step.num ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step.num}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Everything You Need
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Ruler, title: "Size Profiles", desc: "US, UK, EU conversions. All body types." },
              { icon: Palette, title: "Style Matching", desc: "Know their aesthetic before they browse" },
              { icon: Gift, title: "Gift Registry", desc: "Wishlists and gift preferences" },
              { icon: TrendingUp, title: "New Arrival Alerts", desc: "Notify customers of matching items" },
              { icon: Heart, title: "Favorites & History", desc: "Track what they love and buy" },
              { icon: Bell, title: "Restock Alerts", desc: "Notify when sizes are back" },
            ].map((feature) => (
              <Card key={feature.title} className="hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Retail Experience?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join retailers who are building lasting customer relationships with Phygital.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-full px-10 h-14 text-lg font-semibold"
            onClick={() => setShowEarlyAccessModal(true)}
            data-testid="retail-bottom-cta"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* All Categories */}
      <AllCategoriesSection />

      {/* Footer */}
      <SharedFooter />
    </div>
  );
};

export default RetailSalesPage;
